import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { createBandForUser } from "./bandMembershipService";

const ensureUserDocument = async (user, preferredDisplayName = "") => {
  const userRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) return userSnap.data();

  const fallbackDisplayName =
    preferredDisplayName?.trim() ||
    user.displayName ||
    user.email?.split("@")[0] ||
    "Band Member";

  const createdData = {
    createdAt: serverTimestamp(),
    displayName: fallbackDisplayName,
    email: (user.email || "").toLowerCase(),
    lastActiveBandId: null,
  };

  await setDoc(userRef, createdData);
  return createdData;
};

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepDots = ({ current, total }) => (
  <div className="flex gap-1.5 mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={[
          "h-[2px] w-7 rounded-sm transition-[background] duration-300",
          i <= current ? "bg-brand-gold" : "bg-[#2a2a2a]",
        ].join(" ")}
      />
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Login = ({ onLogin, initialMode = "login", authenticatedUser = null }) => {
  // "login" | "signup"
  const [mode] = useState(initialMode);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bandName, setBandName] = useState("");

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const storedInviteCode = (sessionStorage.getItem("pendingInviteCode") || "").trim();
  const storedInviteBandName = (sessionStorage.getItem("pendingInviteBandName") || "").trim();
  const inviteMode = location.state?.inviteMode === true || !!storedInviteCode;
  const inviteBandName = (location.state?.bandName || storedInviteBandName || "").trim();

  const clearMessages = () => { setError(""); setInfo(""); };

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await ensureUserDocument(cred.user);

      // Fetch user doc to get their band context
      const userSnap = await getDoc(doc(db, "users", cred.user.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};

      onLogin(cred.user, userData, {
        inviteMode,
        authEntry: "login",
      });
    } catch (err) {
      if (err.code === "auth/invalid-credential") {
        setError(
          "Login failed. This email/password is invalid for the current Firebase project. If you just switched projects, create the user again or reset their password in Firebase Auth."
        );
      } else if (err.code === "auth/user-not-found") {
        setError("No auth account exists for that email in this Firebase project.");
      } else if (err.code === "auth/wrong-password") {
        setError("Incorrect password. Please try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed login attempts. Wait a moment and try again.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── SIGN UP ────────────────────────────────────────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!displayName.trim()) { setError("Please enter your display name."); return; }
    if (!inviteMode && !bandName.trim()) { setError("Please enter a band name."); return; }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
      await ensureUserDocument(cred.user, displayName);

      if (inviteMode) {
        await onLogin(cred.user, { lastActiveBandId: null }, {
          inviteMode: true,
          authEntry: "signup",
        });
      } else {
        await createBandForUser({ uid: cred.user.uid, bandName: bandName.trim() });
        const userSnap = await getDoc(doc(db, "users", cred.user.uid));
        onLogin(cred.user, userSnap.data());
      }
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("That email is already registered. Try logging in.");
      } else if (err.code === "auth/weak-password") {
        setError("Password must be at least 6 characters.");
      } else {
        setError(err?.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── PASSWORD RESET ─────────────────────────────────────────────────────────
  const handlePasswordReset = async () => {
    clearMessages();
    if (!email) { setError("Enter your email above first."); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo("Reset email sent — check your inbox.");
    } catch {
      setError("Couldn't send reset email. Please try again.");
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-base flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-[420px] bg-surface border border-border border-t-2 border-t-brand-gold rounded-sm px-12 py-14 shadow-[0_24px_80px_rgba(0,0,0,0.7)]">
        <div className="font-display text-[1.1rem] tracking-[0.25em] uppercase text-brand-gold mb-2">
          Band OS
        </div>

        {/* ── LOGIN ── */}
        {mode === "login" && (
          <>
            {inviteMode && inviteBandName ? (
              <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-2">
                {`Sign in to join ${inviteBandName}`}
              </h1>
            ) : (
              <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-2">
                Sign in to Band OS
              </h1>
            )}
            {error && (
              <div className="bg-red-950/50 border border-red-800/50 rounded-sm text-[#e07070] text-[0.88rem] px-4 py-3.5 leading-[1.6] mb-4">
                {error}
              </div>
            )}
            {info && (
              <div className="bg-green-950/50 border border-green-800/50 rounded-sm text-[#70c080] text-[0.82rem] px-3.5 py-2.5 mb-4">
                {info}
              </div>
            )}
            <form onSubmit={handleLogin}>
              <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                Email
              </label>
              <input
                className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@band.com"
                required
                autoFocus
              />
              <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                Password
              </label>
              <div className="relative">
                <input
                  className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-transparent border-0 text-text-muted hover:text-text-primary text-[0.75rem] tracking-[0.08em] cursor-pointer p-0 font-sans"
                >
                  {showPassword ? "HIDE" : "SHOW"}
                </button>
              </div>
              <button
                className="w-full bg-brand-gold text-[#0d0d0c] font-bold border-0 rounded px-4 py-3.5 text-[0.8rem] tracking-[0.18em] uppercase mt-1 hover:bg-brand-gold/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"
                disabled={loading}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
            <button
              className="text-[0.8rem] text-text-muted bg-transparent border-0 p-0 mt-3 hover:text-text-primary cursor-pointer transition-colors"
              onClick={handlePasswordReset}
            >
              Forgot password?
            </button>
            <hr className="border-none border-t border-white/[0.08] my-6" />
            <div className="text-[0.85rem] text-text-muted text-center">
              New here?{" "}
              <button
                className="text-brand-gold bg-transparent border-0 p-0 hover:text-brand-gold/80 cursor-pointer underline"
                onClick={() => { clearMessages(); navigate("/signup", { state: location.state, replace: true }); }}
              >
                Create an account
              </button>
            </div>
          </>
        )}

        {/* ── SIGN UP ── */}
        {mode === "signup" && (
          <>
            {inviteMode && inviteBandName ? (
              <>
                <div className="text-[0.7rem] tracking-[0.18em] uppercase text-brand-gold mb-2">
                  Step 1 of 1
                </div>
                <StepDots current={0} total={1} />
                <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-2">
                  Join {inviteBandName}
                </h1>
                <p className="text-text-secondary text-[0.85rem] mb-7 leading-relaxed">
                  Create your account to continue.
                </p>
              </>
            ) : (
              <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-6">
                Create your account
              </h1>
            )}
            {error && (
              <div className="bg-red-950/50 border border-red-800/50 rounded-sm text-[#e07070] text-[0.88rem] px-4 py-3.5 leading-[1.6] mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSignup}>
              <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                Display Name
              </label>
              <input
                className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                required
                autoFocus
              />
              <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                Email
              </label>
              <input
                className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@band.com"
                required
              />
              <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                Password
              </label>
              <div className="relative">
                <input
                  className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                  type={showSignupPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6+ characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSignupPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-transparent border-0 text-text-muted hover:text-text-primary text-[0.75rem] tracking-[0.08em] cursor-pointer p-0 font-sans"
                >
                  {showSignupPassword ? "HIDE" : "SHOW"}
                </button>
              </div>
              {/* Password strength bar — width and background are truly dynamic */}
              <div
                className="h-[3px] rounded-sm -mt-4 mb-5 transition-[width,background] duration-300"
                style={{
                  width: password.length === 0 ? "0%"
                    : password.length < 6 ? "33%"
                    : password.length < 10 ? "66%"
                    : "100%",
                  background: password.length === 0 ? "transparent"
                    : password.length < 6 ? "#e05555"
                    : password.length < 10 ? "#c8a84b"
                    : "#22c55e",
                }}
              />
              {!inviteMode && (
                <>
                  <hr className="border-none border-t border-white/[0.08] my-6" />
                  <label className="block text-[0.7rem] tracking-[0.18em] uppercase text-text-secondary mb-1.5 font-sans">
                    Your Band
                  </label>
                  <input
                    className="w-full bg-[#181818] border border-white/[0.11] rounded-sm px-3.5 py-3 text-[0.95rem] text-text-primary mb-5 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/10 transition-colors"
                    type="text"
                    value={bandName}
                    onChange={(e) => setBandName(e.target.value)}
                    placeholder="e.g. The Midnight"
                    required
                  />
                </>
              )}
              <button
                className="w-full bg-brand-gold text-[#0d0d0c] font-bold border-0 rounded px-4 py-3.5 text-[0.8rem] tracking-[0.18em] uppercase mt-1 hover:bg-brand-gold/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"
                disabled={loading}
              >
                {loading ? "Creating account…" : "Create Account"}
              </button>
            </form>
            <hr className="border-none border-t border-white/[0.08] my-6" />
            <div className="text-[0.85rem] text-text-muted text-center">
              Already have an account?{" "}
              <button
                className="text-brand-gold bg-transparent border-0 p-0 hover:text-brand-gold/80 cursor-pointer underline"
                onClick={() => { clearMessages(); navigate("/login", { state: location.state, replace: true }); }}
              >
                Sign in
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Login;
